import React from 'react';
import cn from 'classnames';
import { dataroomUrl } from '@dataroom/url';
import DataroomListItem from '../../../domain/DataroomListItem';
import styles from '../datarooms.module.scss';
import stylesForSet from '../../Set/set.module.scss';
import { useDataroomTwoFactorAuthenticationContext } from '@dataroom/authentication/DataroomTwoFactorAuthenticationContext';
import User from '@users/user/User';
import { useSessionContext } from '@users/session/SessionContext';
import { IDataroomListItem } from '../../../domain/vo/DataroomListItem';
import { useDataroomTenantContext } from '@dataroom/tenant/DataroomTenantContext';
import config from '@dataroom/config';
import usersUrl from '@users/url';
import { useDataroomAccessContext } from '../../../contexts/DataroomAccessContext';
import CountryRestrictionTooltip from './CountryRestrictionTooltip';

interface IProps {
  cellProps: {
    row: IDataroomListItem,
    countryId?: string | null,
  },
  isDisabled?: boolean,
}

const NameCell = ({ cellProps, isDisabled = false }: IProps) => {
  const { setModalData, isSessionTwoFactorVerified, userEmail } = useDataroomTwoFactorAuthenticationContext();
  // @ts-ignore
  const { getSession } = useSessionContext();
  const { tenant } = useDataroomTenantContext();
  const { checkDataroomAccess } = useDataroomAccessContext();
  const dataroom = cellProps.row;
  const isNeedToShowTwoFactorModal = !User.isSupportUserByTenant(tenant, userEmail!)
                                     && dataroom.twoFactor
                                     && !isSessionTwoFactorVerified;
  const isDataroomDisabled = DataroomListItem.isDataroomDisabled(dataroom);
  const isSet = tenant === config.investorset.code || tenant === config.verisend.code
    || tenant === config.researchroom.code;
  const tableStyles = isSet ? stylesForSet : styles;
  const { tenantUrlPrefix } = config[tenant].options;
  const url = usersUrl.getSessionPropagateUrl(dataroomUrl(tenant).getUrl(`${ tenantUrlPrefix }/${ dataroom.name }`));

  const showTwoFactorModal = async () => {
    if (isDataroomDisabled) {
      return;
    }
    const session = await getSession({ clearCache: true });
    const visitedDatarooms = session![tenant]?.activeSessions || [];
    const isDataroomVisited = visitedDatarooms.indexOf(dataroom.id) !== -1;
    if (session!.two_factor || isDataroomVisited) {
      window.location.href = url;
      return;
    }
    setModalData({
      isModalOpen: true,
      entityName: dataroom.displayName,
      onSuccessCallback: () => {
        window.location.href = url;
      },
    });
  };

  if (isDisabled) {
    return (
      <>
        <span className={ styles.columnDisable }>{ dataroom.displayName }</span>
        <CountryRestrictionTooltip restrictedCountryIds={ dataroom.restrictedCountryIds } />
      </>
    );
  }

  if (isDataroomDisabled || isNeedToShowTwoFactorModal) {
    return (
      <>
        <span
          className={ cn({
            [tableStyles.deactivatedDataroom]: isDataroomDisabled,
            [tableStyles.link]: !isDataroomDisabled,
          }) }
          onClick={ showTwoFactorModal }
          data-test="showTwoFactorModal"
        >
          { dataroom.displayName }
        </span>
        <CountryRestrictionTooltip restrictedCountryIds={ dataroom.restrictedCountryIds } />
      </>
    );
  }

  const handleClick = async (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    if (e.metaKey || e.ctrlKey) {
      return;
    }

    e.preventDefault();

    if (await checkDataroomAccess(dataroom.id)) {
      window.location.href = url;
    }
  };

  return (
    <>
      <a
        href={ url }
        onClick={ handleClick }
      >
        { dataroom.displayName }
      </a>
      <CountryRestrictionTooltip restrictedCountryIds={ dataroom.restrictedCountryIds } />
    </>
  );
};

export default NameCell;
